import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { S, DataCard } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const emptyPlan = () => ({
  name: '',
  isDefault: false,
  recurringRates: [
    { product: '', rate: 0, basis: 'net_premium' },
  ],
  acquisition: {
    singleProductMultiplier: 1.5,
    multiProductMultiplier: 2.0,
    multiProductThreshold: 2,
    amortizationYears: 0,
    amortizationMethod: 'none',
  },
  status: 'active',
});

const PlansTab = ({ plans, onRefresh }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  const startEdit = (plan) => {
    const copy = JSON.parse(JSON.stringify(plan));
    if (!Array.isArray(copy.recurringRates)) copy.recurringRates = [];
    setEditing(copy);
  };
  const startNew = () => setEditing(emptyPlan());
  const cancelEdit = () => { setEditing(null); setError(null); };

  const updateRateAt = (idx, field, value) => {
    setEditing(prev => {
      const rates = prev.recurringRates.slice();
      rates[idx] = { ...rates[idx], [field]: field === 'rate' ? Number(value) : value };
      return { ...prev, recurringRates: rates };
    });
  };

  const addRateRow = () => {
    setEditing(prev => ({
      ...prev,
      recurringRates: [...prev.recurringRates, { product: '', rate: 0, basis: 'net_premium' }],
    }));
  };

  const removeRateRow = (idx) => {
    setEditing(prev => ({
      ...prev,
      recurringRates: prev.recurringRates.filter((_, i) => i !== idx),
    }));
  };

  const updateAcq = (field, value) => {
    setEditing(prev => ({
      ...prev,
      acquisition: { ...prev.acquisition, [field]: Number(value) },
    }));
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    // Strip out any rows with empty product names or invalid rates.
    const cleaned = {
      ...editing,
      recurringRates: (editing.recurringRates || [])
        .map(r => ({ ...r, product: (r.product || '').trim() }))
        .filter(r => r.product && !isNaN(Number(r.rate))),
    };
    try {
      if (editing._id) {
        await axios.put(`${BASE_URL}/commissions/plans/${editing._id}`, cleaned, { withCredentials: true });
      } else {
        await axios.post(`${BASE_URL}/commissions/plans`, cleaned, { withCredentials: true });
      }
      setEditing(null);
      setToast(t('screens.commissions.plans.saved'));
      onRefresh && onRefresh();
      setTimeout(() => setToast(null), 2500);
    } catch (err) {
      setError(err?.response?.data?.message || t('screens.commissions.plans.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <DataCard style={{ maxWidth: 720 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary, marginBottom: 16 }}>
          {editing._id ? t('screens.commissions.plans.editPlan') : t('screens.commissions.plans.newPlan')}
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase' }}>
            {t('screens.commissions.plans.planName')}
          </span>
          <input
            value={editing.name || ''}
            onChange={e => setEditing(prev => ({ ...prev, name: e.target.value }))}
            style={{
              padding: '9px 12px',
              background: '#fff',
              border: `1px solid ${S.borderLight}`,
              borderRadius: 8,
              fontSize: 13,
              color: S.textPrimary,
              outline: 'none',
            }}
          />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13, color: S.textPrimary }}>
          <input
            type='checkbox'
            checked={!!editing.isDefault}
            onChange={e => setEditing(prev => ({ ...prev, isDefault: e.target.checked }))}
          />
          {t('screens.commissions.plans.isDefault')}
        </label>

        <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary, marginBottom: 8 }}>
          {t('screens.commissions.plans.recurringRates')}
        </div>
        <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 12 }}>
          {t('screens.commissions.plans.productHint')}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: `1px solid ${S.borderLight}` }}>
                {t('screens.commissions.plans.productName')}
              </th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: `1px solid ${S.borderLight}`, width: 160 }}>
                {t('screens.commissions.plans.ratePercent')}
              </th>
              <th style={{ borderBottom: `1px solid ${S.borderLight}`, width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {(editing.recurringRates || []).map((row, idx) => (
              <tr key={idx} style={{ borderBottom: `1px solid ${S.borderLight}` }}>
                <td style={{ padding: '8px 12px' }}>
                  <input
                    value={row.product || ''}
                    onChange={e => updateRateAt(idx, 'product', e.target.value)}
                    placeholder={t('screens.commissions.plans.productNamePh')}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      background: '#fff',
                      border: `1px solid ${S.borderLight}`,
                      borderRadius: 6,
                      fontSize: 13,
                      color: S.textPrimary,
                      outline: 'none',
                    }}
                  />
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type='number'
                      step='0.1'
                      min='0'
                      value={row.rate ?? 0}
                      onChange={e => updateRateAt(idx, 'rate', e.target.value)}
                      style={{
                        flex: 1,
                        padding: '7px 10px',
                        background: '#fff',
                        border: `1px solid ${S.borderLight}`,
                        borderRadius: 6,
                        fontSize: 13,
                        color: S.textPrimary,
                        outline: 'none',
                      }}
                    />
                    <span style={{ fontSize: 12, color: S.textMuted }}>%</span>
                  </div>
                </td>
                <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                  <button
                    onClick={() => removeRateRow(idx)}
                    aria-label='Remove'
                    style={{
                      width: 28,
                      height: 28,
                      background: '#fff',
                      color: S.textMuted,
                      border: `1px solid ${S.borderLight}`,
                      borderRadius: 6,
                      fontSize: 16,
                      lineHeight: 1,
                      cursor: 'pointer',
                    }}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
            {(editing.recurringRates || []).length === 0 && (
              <tr>
                <td colSpan={3} style={{ padding: 16, textAlign: 'center', fontSize: 12, color: S.textMuted }}>
                  {t('screens.commissions.plans.noProducts')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <button
          onClick={addRateRow}
          style={{
            marginBottom: 24,
            padding: '8px 14px',
            background: '#fff',
            color: S.textPrimary,
            border: `1px dashed ${S.borderLight}`,
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + {t('screens.commissions.plans.addProduct')}
        </button>

        <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary, marginBottom: 8 }}>
          {t('screens.commissions.plans.acquisitionRules')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
          {[
            { key: 'singleProductMultiplier', label: 'singleMultiplier', step: '0.1' },
            { key: 'multiProductMultiplier', label: 'multiMultiplier', step: '0.1' },
            { key: 'multiProductThreshold', label: 'multiThreshold', step: '1' },
            { key: 'amortizationYears', label: 'amortizationYears', step: '1' },
          ].map(({ key, label, step }) => (
            <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase' }}>
                {t(`screens.commissions.plans.${label}`)}
              </span>
              <input
                type='number'
                step={step}
                min='0'
                value={editing.acquisition?.[key] ?? 0}
                onChange={e => updateAcq(key, e.target.value)}
                style={{
                  padding: '8px 10px',
                  background: '#fff',
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 6,
                  fontSize: 13,
                  color: S.textPrimary,
                  outline: 'none',
                }}
              />
            </label>
          ))}
        </div>
        <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 20 }}>
          {t('screens.commissions.plans.amortizationNote')}
        </div>

        {error && (
          <div style={{ padding: 10, borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={cancelEdit}
            style={{
              padding: '9px 14px',
              background: '#fff',
              color: S.textSecondary,
              border: `1px solid ${S.borderLight}`,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('screens.commissions.plans.cancel')}
          </button>
          <button
            onClick={save}
            disabled={busy}
            style={{
              padding: '9px 14px',
              background: S.burgundy,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? '…' : t('screens.commissions.plans.save')}
          </button>
        </div>
      </DataCard>
    );
  }

  return (
    <>
      {toast && (
        <div style={{ marginBottom: 16, padding: 10, borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#065f46', fontSize: 13 }}>
          {toast}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: S.textPrimary }}>
          {t('screens.commissions.plans.title')}
        </div>
        <button
          onClick={startNew}
          style={{
            padding: '9px 14px',
            background: S.burgundy,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t('screens.commissions.plans.newPlan')}
        </button>
      </div>

      {plans.length === 0 && (
        <div
          style={{
            padding: '48px 24px',
            textAlign: 'center',
            background: '#fff',
            border: `1px dashed ${S.borderLight}`,
            borderRadius: 12,
            color: S.textMuted,
            fontSize: 13,
          }}
        >
          {t('screens.commissions.plans.empty')}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {plans.map(plan => (
          <div
            key={plan._id}
            onClick={() => startEdit(plan)}
            style={{
              padding: 16,
              background: '#fff',
              borderRadius: 12,
              border: `1px solid ${S.borderLight}`,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = S.burgundy;
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(128,0,32,0.08)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = S.borderLight;
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: S.textPrimary }}>{plan.name}</div>
              {plan.isDefault && (
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: 6,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    background: '#fef3c7',
                    color: '#92400e',
                    textTransform: 'uppercase',
                  }}
                >
                  {t('screens.commissions.plans.defaultBadge')}
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
              {(plan.recurringRates || []).length === 0 ? (
                <div style={{ color: S.textMuted, fontStyle: 'italic' }}>
                  {t('screens.commissions.plans.noProducts')}
                </div>
              ) : (
                (plan.recurringRates || []).map((row, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', color: S.textSecondary }}>
                    <span>{row.product}</span>
                    <span style={{ fontWeight: 700, color: S.burgundy }}>{row.rate}%</span>
                  </div>
                ))
              )}
              <div style={{ borderTop: `1px solid ${S.borderLight}`, marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', color: S.textMuted }}>
                <span>{t('screens.commissions.plans.singleMultiplier')}</span>
                <span style={{ fontWeight: 600, color: S.textPrimary }}>{plan.acquisition?.singleProductMultiplier ?? 1.5}×</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: S.textMuted }}>
                <span>{t('screens.commissions.plans.multiMultiplier')}</span>
                <span style={{ fontWeight: 600, color: S.textPrimary }}>{plan.acquisition?.multiProductMultiplier ?? 2.0}×</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default PlansTab;
